import { RRule } from 'rrule';
import { DateTime } from 'luxon';

import { MarketingTemplateTypeEnum } from '@/bundles/MarketingTemplate/types';
import { formatTimeItem } from '@/utils/filters';

export const marketingCampaignHeadersData: { [key: string]: any }[] = [
  { text: '#', value: '', sortable: false },
  {
    value: 'multi',
    sortable: false,
    multi: [
      {
        text: 'Subject',
        value: 'settings.subject',
        sortable: true,
        single: true,
        icon: 'string',
      },
    ],
  },
  {
    value: 'multi',
    sortable: false,
    multi: [
      {
        text: 'Recipients',
        value: 'recipients.recipient_count',
        sortable: false,
        single: true,
        icon: 'number',
      },
    ],
  },
  {
    value: 'multi',
    sortable: false,
    multi: [
      {
        text: 'Status',
        value: 'status',
        sortable: true,
        single: true,
        icon: 'string',
      },
    ],
  },
  { text: '', value: 'copy', sortable: false },
  {
    value: 'multi',
    sortable: false,
    multi: [
      {
        text: 'Updated',
        value: 'updated.date',
        sortable: true,
        icon: 'date',
      },
      {
        text: 'Created',
        value: 'created.date',
        sortable: true,
        icon: 'date',
      },
    ],
  },
];

const templateTitle = {
  [MarketingTemplateTypeEnum.eblast]: 'Eblast',
  [MarketingTemplateTypeEnum.press_release]: 'Press Release',
  [MarketingTemplateTypeEnum.case_study]: 'Case Study',
  [MarketingTemplateTypeEnum.news]: 'News',
  [MarketingTemplateTypeEnum.website]: 'Website',
  [MarketingTemplateTypeEnum.newsletter]: 'Newsletter',
  [MarketingTemplateTypeEnum.whitepaper]: 'Whitepaper',
  [MarketingTemplateTypeEnum.email]: 'Email',
};

const templateTypeChipColors = {
  [MarketingTemplateTypeEnum.eblast]: '#71c04f',
  [MarketingTemplateTypeEnum.press_release]: 'red',
  [MarketingTemplateTypeEnum.case_study]: '#724fe8',
  [MarketingTemplateTypeEnum.news]: '#f7bf4f',
  [MarketingTemplateTypeEnum.website]: '#199ef5',
  [MarketingTemplateTypeEnum.newsletter]: '#559397',
  [MarketingTemplateTypeEnum.whitepaper]: 'yellow',
  [MarketingTemplateTypeEnum.email]: '#e87200',
}
export const getMarketingTemplateTypeConfig = (type: MarketingTemplateTypeEnum) => {
  return {
    title: type in templateTitle ? templateTitle[type] : 'Template',
    color: templateTypeChipColors[type]
  };
};

export const getMarketingTemplateDraftConfig = (isDraft: boolean) => {
  return {
    title: isDraft ? 'Layout' : 'Template',
    color: isDraft ? 'grey' : 'green'
  };
};

const getTimeZoneHours = (hour: number) => {
  const localHour = (hour - new Date().getTimezoneOffset() / 60) % 24;

  return (localHour + 24) % 24;
}

const convertUTCDateToLocalWithoutTZ = (date: Date | undefined | null): Date | undefined | null => {
  if (!date) return date;

  const localDateTime = DateTime.fromJSDate(date, { zone: 'utc' }).toLocal();

  // create a new UTC Date using the local date/time values, because RRule shows UTC time inside toText() method
  // Jan 16, 23:15 UTC -> becomes Jan 17, 00:15 local; then we create -> Jan 17, 00:15 UTC
  return DateTime.utc(
    localDateTime.year,
    localDateTime.month,
    localDateTime.day,
    localDateTime.hour,
    localDateTime.minute,
    localDateTime.second,
    localDateTime.millisecond
  ).toJSDate();
}

export const parseRRuleString = (rrule: string) => {
  const options = RRule.parseString(rrule);
  let byhour: number | number[] | null | undefined;

  if (Array.isArray(options.byhour)) {
    byhour = options.byhour.map(item => getTimeZoneHours(item));
  } else if (typeof options.byhour === 'number') {
    byhour = getTimeZoneHours(options.byhour);
  } else {
    byhour = options.byhour;
  }

  const dtstart = convertUTCDateToLocalWithoutTZ(options.dtstart);

  if (options.until) {
    const until = convertUTCDateToLocalWithoutTZ(options.until);
    return { ...options, byhour, dtstart, until };
  }

  return {
    ...options,
    byhour,
    dtstart,
  };
};

const getRRuleScheduleTime = (byhour, byminute) => {
  return DateTime.fromFormat(`${formatTimeItem(byhour)}:${formatTimeItem(byminute)}`, 'HH:mm').toFormat('h:mm a');
}

export const getRRuleScheduleText = (rrule: string): string => {
  const optionsWithTZ = parseRRuleString(rrule); // display in the local time zone

  const { byhour, byminute, ...rest } = optionsWithTZ;
  const text = new RRule(rest).toText();

  return `${text} at ${getRRuleScheduleTime(byhour, byminute)}`;
};
